// Dev-only helper: write preset files straight into the repo `assets/` folder via
// the File System Access API, so localhost authoring of predefined devices and
// projects skips the browser download dialog. The chosen `assets/` directory handle
// is persisted in IndexedDB so repeated saves don't re-prompt for the folder.
//
// Only ever invoked from dev-only (`import.meta.env.DEV`) buttons. Chromium-only.

const DB_NAME = 'ecranify-dev'
const STORE_NAME = 'dev-assets'
const HANDLE_KEY = 'assetsRoot'

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(new Error(`IndexedDB open failed: ${req.error?.message ?? 'unknown'}`))
  })

const idbGet = async (key: string): Promise<unknown> => {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).get(key)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(new Error(`IndexedDB read failed: ${req.error?.message ?? 'unknown'}`))
  })
}

const idbSet = async (key: string, value: unknown): Promise<void> => {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put(value, key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(new Error(`IndexedDB write failed: ${tx.error?.message ?? 'unknown'}`))
  })
}

const ensurePermission = async (handle: FileSystemDirectoryHandle): Promise<boolean> => {
  const opts = {mode: 'readwrite'} as const
  if ((await handle.queryPermission(opts)) === 'granted') return true
  return (await handle.requestPermission(opts)) === 'granted'
}

/** Get (or prompt once for) the repo `assets/` directory handle. Must run inside a user gesture. */
export const getAssetsDirHandle = async (): Promise<FileSystemDirectoryHandle> => {
  const stored = (await idbGet(HANDLE_KEY)) as FileSystemDirectoryHandle | undefined
  if (stored && (await ensurePermission(stored))) return stored

  const handle = await window.showDirectoryPicker({mode: 'readwrite'})
  if (!(await ensurePermission(handle))) {
    throw new Error('Permission to write into the assets folder was denied')
  }
  await idbSet(HANDLE_KEY, handle)
  return handle
}

/** Write a file at `relPath` (slash-separated, relative to the assets root), creating subdirs. */
export const writeFileToAssets = async (
  root: FileSystemDirectoryHandle,
  relPath: string,
  data: Blob | string,
): Promise<void> => {
  const parts = relPath.split('/').filter(Boolean)
  const fileName = parts.pop()
  if (!fileName) throw new Error(`Invalid asset path: ${relPath}`)
  let dir = root
  for (const part of parts) {
    dir = await dir.getDirectoryHandle(part, {create: true})
  }
  const fileHandle = await dir.getFileHandle(fileName, {create: true})
  const writable = await fileHandle.createWritable()
  await writable.write(data)
  await writable.close()
}
